import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
const DAY_MS = 24 * 60 * 60 * 1000;
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + DAY_MS;
  const stored = window.localStorage.getItem('mp_countdown_target');
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!isNaN(parsed) && parsed > Date.now()) return parsed;
  }
  const target = Date.now() + DAY_MS;
  try {
    window.localStorage.setItem('mp_countdown_target', target.toString());
  } catch {}
  return target;
}
function pad(n: number) {
  return n.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(DAY_MS);
  useEffect(() => {
    setTarget(getInitialTarget());
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        const next = Date.now() + DAY_MS;
        try {
          window.localStorage.setItem('mp_countdown_target', next.toString());
        } catch {}
        setTarget(next);
        setRemaining(DAY_MS);
      } else {
        setRemaining(diff);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  const totalHours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const elapsed = DAY_MS - remaining;
  const progress = Math.min(90, 65 + elapsed / DAY_MS * 25);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} className="mt-10 max-w-3xl mx-auto bg-paper border border-terracotta/30 rounded-2xl p-6 md:p-8" data-id="element-44" data-mp="1l13d1n2d7cxe">
      <div className="text-center" data-id="element-45" data-mp="2sc7lbtp5nc6o">
        <div className="flex items-center justify-center gap-2 text-terracotta-deep font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-46" data-mp="2sad8z3ry8se7">
          <AlarmClock size={20} aria-hidden="true" data-id="element-47" data-mp="2jazx64gcrrqb" />
          <span data-id="element-48" data-mp="2ecyirwtvggl6">Launch pricing ends in:</span>
        </div>

        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" role="timer" aria-live="off" data-id="element-49" data-mp="2ecgce7dhwlzj">
          <TimeBox value={pad(totalHours)} label="hrs" data-id="element-50" data-mp="24nez4oyc4ip9" />
          <span className="text-3xl md:text-5xl font-bold text-terracotta" aria-hidden="true" data-id="element-51" data-mp="24lu3ew0aatpt">
            :
          </span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-52" data-mp="1vn9qyclbhga1" />
          <span className="text-3xl md:text-5xl font-bold text-terracotta" aria-hidden="true" data-id="element-53" data-mp="1vkduksp4aznp">
            :
          </span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-54" data-mp="1lpc6m06kw3mp" />
        </div>

        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-charcoal-soft mb-8" data-id="element-55" data-mp="16iaz6e7ched">
          After this the price goes up and it will not come back down.
        </p>

        <div className="space-y-2" data-id="element-56" data-mp="b4fzzw3vyi81">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-57" data-mp="b5hcm50jvcrv">
            <span className="text-charcoal-muted" data-id="element-58" data-mp="f4q6razi5cny">
              Launch price spots remaining
            </span>
            <span className="text-burnt" data-id="element-59" data-mp="3emptaljjk6l0">Only a few left</span>
          </div>
          <div className="w-full h-2.5 bg-sand rounded-full overflow-hidden border border-warmgray" data-id="element-60" data-mp="3jlarygj5ai7c">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="h-full bg-burnt rounded-full" data-id="element-61" data-mp="3nkrxf9yknfci" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-charcoal-muted font-medium" data-id="element-62" data-mp="2gpato5gw7r2l">
            {Math.round(progress)}% of spots taken
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-sand border border-terracotta/30 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px]" data-id="element-63" data-mp="2loncmf4n8dzw">
      <div className="flex items-baseline justify-center gap-1" data-id="element-64" data-mp="2ujadqz39y46c">
        <span className="text-4xl md:text-5xl font-bold text-charcoal tabular-nums" data-id="element-65" data-mp="2ul4oxloquhx8">
          {value}
        </span>
        <span className="text-xs md:text-sm font-medium text-terracotta-dark" data-id="element-66" data-mp="39dye5ytticf3">
          {label}
        </span>
      </div>
    </div>;
}